import type { FormSubmissionPayload } from './submissionPayload';

// Overseer's marketing form ingestion endpoint (overseer#51673). The site build inlines
// NEXT_PUBLIC_FORM_SUBMISSIONS_BASE_URL, which scripts/checkEnv.js requires on every build.
// Where no Next build runs — Storybook, Jest, the Playwright gallery — the variable is unset
// and this falls back to staging, so nothing outside a site build can write production data.
const BASE_URL = process.env.NEXT_PUBLIC_FORM_SUBMISSIONS_BASE_URL || 'https://staging1.sensortower.com';

export const FORM_SUBMISSIONS_URL = `${BASE_URL}/api/marketing/form_submissions`;

// Long enough for a slow connection, short enough that the disabled submit button
// cannot appear hung indefinitely on a stalled request.
const SUBMIT_TIMEOUT_MS = 15_000;

/**
 * 'rateLimited' — HTTP 429, the endpoint's per-IP throttle (15 requests / 5 minutes).
 * 'failed' — any other non-2xx, plus network errors and timeouts. The endpoint stores nothing
 * on failure, so all of these mean the same thing to the visitor: nothing was recorded, retry.
 */
export type SubmitFormResult = 'success' | 'rateLimited' | 'failed';

export const submitFormPayload = async (payload: FormSubmissionPayload): Promise<SubmitFormResult> => {
  try {
    const response = await fetch(FORM_SUBMISSIONS_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ payload }),
      // The endpoint is open by design and never reads a session — sending cookies
      // cross-origin would only leak them.
      credentials: 'omit',
      signal: AbortSignal.timeout(SUBMIT_TIMEOUT_MS),
    });
    if (response.status === 429) return 'rateLimited';
    if (!response.ok) {
      // The visitor gets the generic message either way; the status is for whoever is
      // debugging a dropped lead from the field, since no error monitoring runs in this package.
      // eslint-disable-next-line no-console
      console.error(`Form submission failed with status ${response.status}`);
      return 'failed';
    }
    return 'success';
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Form submission request failed', error);
    return 'failed';
  }
};
